import { Link } from "@/components/marketing/elements/link"
import { NoIndexHead } from "@/components/marketing/NoIndexHead"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { MinimalMarketingLayout } from "@/components/marketing/MinimalMarketingLayout"
import { DocumentCentered } from "@/components/marketing/sections/document-centered"
import { isLaunchMode } from "@/lib/launch-config"
import { legalAepdUrl, legalOperator } from "@/lib/legal-config"
import { appDomain } from "@/lib/site-links"
import { getUmamiConfig, isAnalyticsOptedOut, setAnalyticsOptOut } from "@/lib/umami"

function AnalyticsOptOutControl() {
  if (!getUmamiConfig()) return null

  if (isAnalyticsOptedOut()) {
    return (
      <button
        type="button"
        onClick={() => {
          setAnalyticsOptOut(false)
          window.location.reload()
        }}
        className="mt-3 text-sm font-medium text-mauve-950 underline underline-offset-2 dark:text-white"
      >
        Re-enable analytics on this site
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={() => {
        setAnalyticsOptOut(true)
        window.location.reload()
      }}
      className="mt-3 text-sm font-medium text-mauve-950 underline underline-offset-2 dark:text-white"
    >
      Opt out of analytics on this site
    </button>
  )
}

export function PrivacyPolicyPage() {
  const umamiConfigured = getUmamiConfig() !== null
  const Layout = isLaunchMode() ? MinimalMarketingLayout : MarketingLayout

  return (
    <Layout>
      <NoIndexHead />
      <DocumentCentered
        headline="Privacy policy"
        subheadline={<p>Last updated: August 2026. Plain summary of what ArtlessDev collects and why.</p>}
      >
        <div className="space-y-8 text-sm/7 text-mauve-700 dark:text-mauve-400">
          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">Operator and contact</h2>
            <p className="mt-3">
              {legalOperator.name} is the legal operator of ArtlessDev.
            </p>
            <ul className="mt-3 list-none space-y-1">
              <li>CIF: {legalOperator.cif}</li>
              <li>{legalOperator.address}</li>
              <li>
                Email:{" "}
                <Link href={`mailto:${legalOperator.email}`}>{legalOperator.email}</Link>
              </li>
            </ul>
            <p className="mt-3">ArtlessDev is developed at Binskea. The app is available at {appDomain}.</p>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">What ArtlessDev does</h2>
            <p className="mt-3">
              ArtlessDev is a browser-based service for writing and browsing structured behavioral scenarios —
              for teams that want a shared, readable record of how their software behaves.
            </p>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">What we collect</h2>
            <ul className="mt-3 list-disc space-y-2 pl-5">
              <li>
                <strong className="font-medium text-mauve-950 dark:text-white">Account data</strong> — email address
                and password (or GitHub profile if you sign in with GitHub) via Supabase Auth.
              </li>
              <li>
                <strong className="font-medium text-mauve-950 dark:text-white">Workspace data</strong> — workspace and
                project names, member lists, invitations, and the scenarios you create (titles, evidence,
                relationships, and status) stored in the app. We do not sell this content.
              </li>
              <li>
                <strong className="font-medium text-mauve-950 dark:text-white">Error reports</strong> — in the signed-in
                app, we may send anonymized crash data to Sentry to fix bugs.
              </li>
              {umamiConfigured ? (
                <li>
                  <strong className="font-medium text-mauve-950 dark:text-white">Marketing analytics</strong> — on public
                  pages (landing, knowledge base, legal pages), see the{" "}
                  <a href="#analytics" className="underline underline-offset-2">
                    analytics section
                  </a>{" "}
                  below.
                </li>
              ) : null}
            </ul>
          </section>

          {umamiConfigured ? (
            <section id="analytics">
              <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">Website analytics (Umami)</h2>
              <p className="mt-3">
                On our public marketing pages we use{" "}
                <a
                  href="https://umami.is/privacy"
                  className="underline underline-offset-2"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Umami Cloud
                </a>
                , a privacy-focused analytics platform. It helps us understand which pages visitors read — for example,
                whether the knowledge base is useful — without running ads or building marketing profiles.
              </p>
              <p className="mt-3">
                <strong className="font-medium text-mauve-950 dark:text-white">What Umami may record:</strong> page URL,
                referrer, browser type, operating system, device type, language, and approximate country (from IP, then
                discarded). Umami does not use cookies for tracking and does not collect names, emails, or other directly
                identifying information on these pages.
              </p>
              <p className="mt-3">
                <strong className="font-medium text-mauve-950 dark:text-white">Legal basis (EEA/UK):</strong> legitimate
                interest in operating and improving the product, balanced against your privacy; you may opt out at any
                time.
              </p>
              <p className="mt-3">
                <strong className="font-medium text-mauve-950 dark:text-white">Scope:</strong> analytics run on marketing
                pages only, not inside the signed-in app. We honor browser Do Not Track when enabled. Where Umami
                processes data outside the EEA, appropriate safeguards such as Standard Contractual Clauses may apply.
              </p>
              <p className="mt-3">
                <strong className="font-medium text-mauve-950 dark:text-white">Your choices:</strong> use the site footer,
                the notice on your first visit, or the control below to opt out. Opt-out is stored in your browser&apos;s
                local storage on this device.
              </p>
              <AnalyticsOptOutControl />
            </section>
          ) : null}

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">What we do not do</h2>
            <ul className="mt-3 list-disc space-y-2 pl-5">
              <li>We do not sell your data or scenario content to third parties.</li>
              <li>We do not process payments during beta (billing is not active).</li>
              <li>We do not use your content to train AI models.</li>
              {umamiConfigured ? (
                <li>We do not use ad trackers or cross-site profiling on our marketing pages.</li>
              ) : null}
            </ul>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">How we use data</h2>
            <p className="mt-3">
              To run the service: authenticate you, store and display your scenarios, enforce team permissions, and
              improve reliability. We may email you about your account or important product changes.
              {umamiConfigured
                ? " Aggregate page-view statistics help us improve public documentation and landing content."
                : null}
            </p>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">Retention</h2>
            <p className="mt-3">
              Account and workspace data are kept while your account is active and as needed to provide the service.
              {umamiConfigured
                ? " Analytics data is retained according to Umami configuration and operational needs."
                : null}{" "}
              You may request deletion by contacting us.
            </p>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">Third-party services</h2>
            <ul className="mt-3 list-disc space-y-2 pl-5">
              <li>Supabase — hosting, database, and authentication</li>
              <li>GitHub — optional sign-in via GitHub OAuth</li>
              <li>Sentry — optional error monitoring in the signed-in app</li>
              {umamiConfigured ? <li>Umami Cloud — privacy-friendly analytics on public marketing pages</li> : null}
            </ul>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">Your rights</h2>
            <p className="mt-3">
              You may request access, rectification, erasure, objection, restriction, and portability where applicable by
              contacting{" "}
              <Link href={`mailto:${legalOperator.email}`}>{legalOperator.email}</Link>.
              {umamiConfigured
                ? " For marketing analytics, use the opt-out control in the analytics section above or in the site footer."
                : null}
            </p>
            <p className="mt-3">
              You may lodge a complaint with the Spanish Data Protection Authority (AEPD):{" "}
              <a href={legalAepdUrl} className="underline underline-offset-2" rel="noopener noreferrer" target="_blank">
                {legalAepdUrl}
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">Changes</h2>
            <p className="mt-3">
              We may update this page as the product grows. Material changes will be noted here with a new date.
            </p>
          </section>

          <section>
            <h2 className="text-base/8 font-medium text-mauve-950 dark:text-white">Applicable law</h2>
            <p className="mt-3">
              This privacy information is governed by Spanish law. If mandatory consumer protection or local laws apply
              in your country, those mandatory rules continue to apply where required.
            </p>
          </section>
        </div>
      </DocumentCentered>
    </Layout>
  )
}
